import { z } from 'zod';

import { areAttendeesEqual, attendeeSchema, type Attendee } from './Attendee';

export const DEFAULT_DISCOUNT_PERCENTAGE = 0;
export const DISCOUNT_MORE_THAN_N_ATTENDEES = 99999;

const appointmentSchema = z.object({
	start: z.string(),
	end: z.string(),
});

export const courseCartItemSchema = z.object({
	id: z.string(),
	slug: z.string().min(1),
	title: z.string().min(1),
	typeSlug: z.string().min(1),
	typeTitle: z.string().min(1),
	appointment: appointmentSchema,
	attendees: z.array(attendeeSchema),
	price: z.number().min(0),
});

export type CourseCartItem = z.infer<typeof courseCartItemSchema>;

export function calculatePricePerAttendee(cartItem: CourseCartItem): number[] {
	const discountedPrice = (cartItem.price * (100 - DEFAULT_DISCOUNT_PERCENTAGE)) / 100;

	return cartItem.attendees.map((_, idx) => {
		const attendeeNum = idx + 1;
		return attendeeNum < DISCOUNT_MORE_THAN_N_ATTENDEES ? cartItem.price : discountedPrice;
	});
}

export function calculateTotalPrice(cartItem: CourseCartItem): number {
	return calculatePricePerAttendee(cartItem).reduce((acc, curr) => acc + curr, 0);
}

export function calculateTotalPriceWithoutDiscount(cartItem: CourseCartItem): number {
	return cartItem.attendees.length * cartItem.price;
}

export function hasAttendees(cartItem: CourseCartItem): boolean {
	return (cartItem.attendees?.length ?? 0) > 0;
}

export function areCartItemsEqual(item1: CourseCartItem, item2: CourseCartItem): boolean {
	return (
		item1.id === item2.id &&
		item1.slug === item2.slug &&
		item1.title === item2.title &&
		item1.typeSlug === item2.typeSlug &&
		item1.typeTitle === item2.typeTitle &&
		item1.price === item2.price &&
		item1.appointment.start === item2.appointment.start &&
		item1.appointment.end === item2.appointment.end
		// do not check attendees here!
	);
}

export function addAttendee(cartItem: CourseCartItem, attendee: Attendee): CourseCartItem {
	const updated = courseCartItemSchema.parse({
		...cartItem,
		attendees: [...cartItem.attendees, attendee],
	});
	window?.plausible?.('cart_item_attendee_added', {
		props: {
			course_type_slug: cartItem.typeSlug,
			course_slug: cartItem.slug,
			new_attendee_count: String(updated.attendees.length),
			total_price: String(calculateTotalPrice(updated)),
		},
	});
	return updated;
}

export function removeAttendee(
	cartItem: CourseCartItem,
	attendeeToRemove: Attendee,
): CourseCartItem {
	const remainingAttendees = cartItem.attendees.filter((attendee) => {
		const shouldKeep = !areAttendeesEqual(attendee, attendeeToRemove);
		return shouldKeep;
	});

	const updated = courseCartItemSchema.parse({
		...cartItem,
		attendees: remainingAttendees,
	});

	if (cartItem.attendees.length !== remainingAttendees.length) {
		window?.plausible?.('cart_item_attendee_removed', {
			props: {
				course_type_slug: cartItem.typeSlug,
				course_slug: cartItem.slug,
				new_attendee_count: String(remainingAttendees.length),
				total_price: String(calculateTotalPrice(updated)),
			},
		});
	}

	return updated;
}

export interface CreateCourseCartItemInput {
	id: string;
	slug: string;
	title: string;
	typeSlug: string;
	typeTitle: string;
	appointment: {
		start: string;
		end: string;
	};
	price: number;
	attendees?: Attendee[];
}

export function createCourseCartItem(input: CreateCourseCartItemInput): CourseCartItem {
	return courseCartItemSchema.parse({
		...input,
		attendees: input.attendees?.filter(Boolean) ?? [],
	});
}

// Serialization helpers
export function fromJSON(json: unknown): CourseCartItem {
	return courseCartItemSchema.parse(json);
}
